/**
 * Persist a Wikidata-linked entity into the KB index (entity_summaries)
 * so GET /api/documents/entities includes it.
 */

import {saveEntitySummary} from '../firebase/firestore/entityWriters';
import {fetchWikidataSummary} from './wikidataService';

const WIKIDATA_ID_RE = /^Q\d+$/i;

export function normalizeWikidataId(raw) {
    if (raw == null || raw === '') return null;
    const id = String(raw).trim().toUpperCase();
    return WIKIDATA_ID_RE.test(id) ? id : null;
}

/**
 * Build a minimal summary from task / search result payload.
 * @param {Object} entity
 * @returns {Object|null}
 */
export function buildSummaryFromEntityPayload(entity) {
    const qid = normalizeWikidataId(entity?.id ?? entity?.wikidataId);
    if (!qid) return null;

    const canonicalName = (entity?.label ?? entity?.canonicalName ?? '').trim();
    if (!canonicalName) return null;

    return {
        wikidataId: qid,
        canonicalName,
        description: entity?.description ?? null,
        image: entity?.imageUrl ?? entity?.image ?? null,
        aliases: Array.isArray(entity?.aliases) ? entity.aliases : [],
        entityType: entity?.entityType ?? null,
        source: 'wikidata',
    };
}

/**
 * Upsert KB summary for a Q-id. Prefers full Wikidata hydrate; falls back to inline task fields.
 *
 * @param {string|Object} entityOrQid - Q-id string or { id, label, description, imageUrl, ... }
 * @param {{ preferFullHydrate?: boolean }} [options]
 * @returns {Promise<void>}
 */
export async function syncKbEntityFromWikidata(entityOrQid, options = {}) {
    const {preferFullHydrate = true} = options;
    const qid = typeof entityOrQid === 'string'
        ? normalizeWikidataId(entityOrQid)
        : normalizeWikidataId(entityOrQid?.id ?? entityOrQid?.wikidataId);

    if (!qid) return;

    try {
        if (preferFullHydrate) {
            const result = await fetchWikidataSummary(qid);
            if (result?.summary) {
                await saveEntitySummary(qid, result.summary);
                return;
            }
        }

        const stub = typeof entityOrQid === 'object'
            ? buildSummaryFromEntityPayload(entityOrQid)
            : null;
        if (stub) {
            await saveEntitySummary(qid, stub);
        }
    } catch (err) {
        console.warn('[syncKbEntityFromWikidata] KB summary sync failed:', err);
    }
}
